<template>
  <form @submit.prevent="$emit('submit', data)">
    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            General Information
          </h3>

          <p>
            In this panel, you can modify the core data of the event.
          </p>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <TextFormGroup
                v-model="data.title"
                name="title"
                label="Title"
                help-text="The title of the event."
                :error="(errors.title || [])[0]"
            />

            <TextareaFormGroup
                v-model="data.subheading"
                name="subheading"
                label="Subheading"
                help-text="The subheading of the event."
                :error="(errors.subheading || [])[0]"
            />

            <GalleryFormGroup
                v-model="data.image_id"
                name="image_id"
                label="Top Image"
                help-text="The image of the event."
                :error="(errors.image_id || [])[0]"
                :nullable="true"
                :company-id="companyId"
            />

            <TextareaFormGroup
                v-model="data.image_text"
                name="image_text"
                label="Top Image Text"
                help-text="The image text of the event."
                :error="(errors.image_text || [])[0]"
            />

            <TextFormGroup
                v-model="data.video_url"
                name="video_url"
                label="Top Video URL"
                help-text="The URL of a video to include in the event."
                :error="(errors.video_url || [])[0]"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Event Details
          </h3>

          <p>
            In this panel, you can enter the specific details about the event.
          </p>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <SelectFormGroup
                v-model="data.content_event_category_id"
                name="content_event_category_id"
                label="Category"
                :error="(errors.content_event_category_id || [])[0]"
                :options="contentEventCategoryOptions"
                :nullable="true"
            />

            <TextFormGroup
                v-model="data.location"
                name="location"
                label="Location"
                help-text="The location of the event."
                :error="(errors.location || [])[0]"
            />

            <TextFormGroup
                v-model="data.link"
                name="link"
                label="Submit Link"
                help-text="A link to the event."
                :error="(errors.link || [])[0]"
            />

            <DateFormGroup
                v-model="data.start_date"
                name="start_date"
                label="Start Date"
                help-text="The date that the event starts."
                :error="(errors.start_date || [])[0]"
                format="YYYY-MM-DD"
                original-format="YYYY-MM-DD"
            />

            <TextFormGroup
                v-model="data.start_time"
                name="start_time"
                label="Start Time"
                help-text="The time that the event starts."
                :error="(errors.start_time || [])[0]"
            />

            <DateFormGroup
                v-model="data.end_date"
                name="end_date"
                label="End Date"
                help-text="The date that the event ends."
                :error="(errors.end_date || [])[0]"
                format="YYYY-MM-DD"
                original-format="YYYY-MM-DD"
            />

            <TextFormGroup
                v-model="data.end_time"
                name="end_time"
                label="End Time"
                help-text="The time that the event ends."
                :error="(errors.end_time || [])[0]"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Body
          </h3>

          <p>
            These panels contain the main text of the event posting. You can add more blocks to the main text by using
            the button below.
          </p>

          <div class="btn-group" style="margin-top: 4px">
            <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
              <i class="fa fa-plus"></i> Add <span class="caret"></span>
            </button>

            <ul class="dropdown-menu">
              <li v-for="bodyCategoryOption in bodyCategoryOptions">
                <a
                    href="#"
                    @click.prevent="data.content_bodies.push({body_category: bodyCategoryOption.value, body_category_parameters: {}})"
                >
                  {{ bodyCategoryOption.label }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Draggable
          element="div"
          v-model="data.content_bodies"
          handle=".handle"
          chosen-class="interval--dragging"
          class="col-xs-18"
      >
        <ContentBodyFormPanel
            v-for="(contentBody, index) in data.content_bodies"
            v-model="data.content_bodies[index]"
            :key="index"
            :errors="getBodyCategoryParametersErrors(index)"
            :initial-collapsed="true"
            :company-id="companyId"
            @remove="data.content_bodies.splice(index, 1)"
        />
      </Draggable>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Publication
          </h3>

          <p>
            In this panel, you can adjust the publication settings for the event.
          </p>

          <button v-if="edit" type="button" class="btn btn-black btn-sm" @click="$emit('copy')">
            <i class="fa fa-share"></i> Share
          </button>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <DateFormGroup
                v-model="data.publish_at"
                name="publish_at"
                label="Publish At"
                help-text="The date that the event should be published."
                :error="(errors.publish_at || [])[0]"
                format="YYYY-MM-DD HH:mm:ss"
                original-format="YYYY-MM-DD HH:mm:ss"
            />

            <DateFormGroup
                v-model="data.unpublish_at"
                name="unpublish_at"
                label="Unpublish At"
                help-text="The date that the event should be unpublished."
                :error="(errors.unpublish_at || [])[0]"
                format="YYYY-MM-DD HH:mm:ss"
                original-format="YYYY-MM-DD HH:mm:ss"
            />

            <StaticFormGroup
                v-if="edit"
                label="Author"
                :value="author"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Advanced Setup
          </h3>

          <p>
            These settings control the advanced setup for the event.
          </p>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <StaticFormGroup
                v-if="edit"
                :value="data.path"
                label="Path"
                help-text="The path of the event."
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-offset-6 col-xs-18">

        <button type="submit" class="btn btn-black pull-left" style="margin-bottom: 22px">
          <i class="fa fa-save"></i> Save
        </button>

        <a
            v-if="url"
            :href="url + '?preview=true'"
            class="btn btn-white pull-right"
            target="_blank"
            style="border: 1px solid #000000;"
        >
          <i class="fas fa-external-link-alt"></i> Preview
        </a>

        <div class="clearfix"></div>
      </div>
    </div>
  </form>
</template>

<script>
import store from '@/store'
import BodyCategoryParameterForm from "../../ContentBodies/BodyCategoryParameterForms/Base.vue";
import ContentBodyFormPanel from "../../ContentBodies/Panels/Form.vue";
import DateFormGroup from '@/vendor/forms/FormGroups/Block/Date'
import Draggable from 'vuedraggable'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Block/Submit'
import TextareaFormGroup from '@/vendor/forms/FormGroups/Block/Textarea'
import TextFormGroup from '@/vendor/forms/FormGroups/Block/Text'
import RepeatableFormGroup from "../../../Forms/RepeatableFormGroup.vue";
import SelectFormGroup from "../../../../vendor/forms/FormGroups/Block/Select.vue";
import RadioFormGroup from "../../../../vendor/forms/FormGroups/Block/Radio.vue";
import StaticFormGroup from "../../../../vendor/forms/FormGroups/Block/Static.vue";
import GalleryFormGroup from "../../../FormGroups/Block/Gallery"

export default {
  components: {
    Draggable,
    SelectFormGroup,
    RadioFormGroup,
    RepeatableFormGroup,
    DateFormGroup,
    SubmitFormGroup,
    TextareaFormGroup,
    TextFormGroup,
    BodyCategoryParameterForm,
    ContentBodyFormPanel,
    GalleryFormGroup,
    StaticFormGroup
  },

  props: {
    initial: {
      required: false,
      type: Object,
      default: () => ({})
    },

    errors: {
      required: true,
      type: Object
    },

    edit: {
      required: false,
      default: false
    },

    url: {
      required: false,
      type: String
    },

    contentEventCategories: {
      required: true,
      type: Array
    },

    companyId: {
      required: true,
      type: [String, Number]
    },
  },

  data() {
    return {
      data: {...this.initial},

      shared: store
    }
  },

  methods: {
    getBodyCategoryParametersErrors(index) {
      return Object.entries(this.errors)
          .filter(entry => entry[0].startsWith('content_bodies.' + index + '.body_category_parameters.'))
          .reduce((result, entry) => ({
            ...result,
            [entry[0].substring(('content_bodies.' + index + '.body_category_parameters.').length)]: entry[1]
          }), {})
    },
  },

  computed: {
    bodyCategoryOptions() {
      return [
        {value: 'text', label: 'Text'},
        {value: 'fact-box', label: 'Fact Box'},
        {value: 'quote', label: 'Quote'},
        {value: 'image', label: 'Image'},
        {value: 'gallery', label: 'Gallery'},
        {value: 'podcast', label: 'Podcast'},
        {value: 'social-media', label: 'Social Media'},
        {value: 'video', label: 'Video'},
      ]
    },

    contentEventCategoryOptions() {
      return this.contentEventCategories.map(contentEventCategory => ({
        value: contentEventCategory.id,
        label: contentEventCategory.attributes.name
      }))
    },

    author() {
      return [
        this.data.author_name,
        this.data.author_email,
      ].join('&nbsp;&middot;&nbsp;')
    }
  }
}
</script>